import React         from 'react';
import { UTC }       from 'rewire-common';
import { Observe }   from 'rewire-core';
import { Theme }     from '@material-ui/core/styles';
import {
  IFieldDefn,
  IFormContext
} from 'rewire-ui';
import {
	IWeekDay,
	IWeekDayNum,
}                              from '../DataTypes';
import mainPageModel           from '../main-navigation/MainPageModel';
import { localTimeZoneUTCNow } from '../utility/DateUtils';
import { makeStyles }          from '../utility/styles';
import { PickerToolbar, ToolbarButton } from './materialPickerInterop';

export type IDateSelector               = 'Last Week' | 'This Week' | 'Yesterday' | 'Today';
export const defaultWeekStart: IWeekDay = 'Monday';

export function getUTCFromDateSelector(dateSelector: IDateSelector): UTC {
  let newUTCValue: UTC;
  switch(dateSelector) {
    case 'Last Week': {
      const curr           = localTimeZoneUTCNow();
      const lastWeekOffset = IWeekDayNum[mainPageModel.location?.weekStart ?? defaultWeekStart] - curr.toUTCDate().getDay() - 7;
      newUTCValue          = curr.add(lastWeekOffset);
      break;
    }
    case 'This Week': {
      const currUTC        = localTimeZoneUTCNow();
      const thisWeekOffset = IWeekDayNum[mainPageModel.location?.weekStart ?? defaultWeekStart] - currUTC.toUTCDate().getDay();
      newUTCValue          = currUTC.add(thisWeekOffset);
      break;
    }
    case 'Yesterday':
      newUTCValue = localTimeZoneUTCNow().add(-1);
      break;
    case 'Today':
      newUTCValue = localTimeZoneUTCNow();
      break;
  }

  return newUTCValue;
}

const toolbarStyles = (_theme: Theme) => ({
	toolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    height: '115px',
    padding: '10px 20px',
  },
  buttonContainerDefault: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  buttonContainerCustom: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
});

const useToolbarStyles = makeStyles(toolbarStyles);

const CustomDatePickerToolbar = (props: any) => {

	const { date, isLandscape, openView, setOpenView, title, onChange } = props;

	const handleChangeViewClick = (view: 'date' | 'year') => () => {
		setOpenView(view);
  };

  const handleDateSelectorClick = (dateSelector: IDateSelector) => () => {
    mainPageModel.dateSelector = dateSelector;
    const newUTCValue          = getUTCFromDateSelector(dateSelector);
    const newDateValue         = newUTCValue ? newUTCValue.toUTCDate() : null;
    onChange(newDateValue);
  };

  const classes   = useToolbarStyles();
  const year      = date.getUTCFullYear();
  const yearLabel = `${year}`;
  const dateLabel = date.toLocaleDateString('en-us', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

	return (
    <PickerToolbar className={classes.toolbar} title={title} isLandscape={isLandscape}>
      <div className={classes.buttonContainerDefault}>
        <ToolbarButton
          onClick={handleChangeViewClick('year')}
          variant='h6'
          label={yearLabel}
          selected={openView === 'year'}
        />
        <ToolbarButton
          onClick={handleChangeViewClick('date')}
          variant='h4'
          selected={openView === 'date'}
          label={dateLabel}
        />
      </div>
      <Observe render={() => (
        <div className={classes.buttonContainerCustom}>
          <ToolbarButton
            onClick={handleDateSelectorClick('Last Week')}
            variant='body2'
            label={'Last Week'}
            selected={mainPageModel.dateSelector === 'Last Week'}
          />
          <ToolbarButton
            onClick={handleDateSelectorClick('This Week')}
            variant='body2'
            label={'This Week'}
            selected={mainPageModel.dateSelector === 'This Week'}
          />
          <ToolbarButton
            onClick={handleDateSelectorClick('Yesterday')}
            variant='body2'
            label={'Yesterday'}
            selected={mainPageModel.dateSelector === 'Yesterday'}
          />
          <ToolbarButton
            onClick={handleDateSelectorClick('Today')}
            variant='body2'
            label={'Today'}
            selected={mainPageModel.dateSelector === 'Today'}
          />
        </div>
      )} />
    </PickerToolbar>
	);
};

export const getCustomFormDatePicker = (form: IFormContext, editProps?: any): IFieldDefn => {
  const dateOptions = {
    ToolbarComponent: CustomDatePickerToolbar,
    ...editProps,
  };
  return form.date(dateOptions);
};
